import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Block,
  RowFragment,
  TableAppendixBlockData,
  TableAppendixColumn,
  TableAppendixRow,
  TablePhotoOption,
  PHOTO_LABEL,
  blankTableRow,
  headingColumn,
  tableRowHasContent,
  toggleTableRowKind,
  formatImageRefs,
  itemNumbers,
  photoNumbers,
  tableImageCell,
  setRowPhoto,
  visibleTableColumns,
} from '../../../config';
import { isTableRowOnAPlan } from '../../../utils/planMarkers';
import LiveText, { notifyLiveTextInput } from '../../LiveText';
import { readSubscripts } from '../../../subscript';
import RowPhotoPicker from '../RowPhotoPicker';
import '../appendix-pages.scss';
import './table-appendix-page.scss';

/**
 * The layout key a table cell writes into data-layout-key, and the key
 * DocumentEditor's export collects its measured lines under. One scheme, so
 * DocumentPDF finds the cell's lines without knowing how they were gathered.
 */
export const tableCellLayoutKey = (blockId: string, rowId: string, columnId: string): string =>
  `${blockId}:${rowId}:${columnId}`;

interface TableAppendixPageProps {
  block: Block;
  /** The whole document, for the photo numbers the Image column derives from. */
  blocks: Block[];
  /** photoNumbers(blocks), when the caller has already worked it out. */
  numbers?: Map<string, number>;
  /** Which rows this page shows. Absent: the whole table (the measurement copy). */
  fragment?: RowFragment;
  isEditing: boolean;
  onChange?: (data: TableAppendixBlockData) => void;
  /** Every photo in the document, for a row's photo picker (editing only). */
  photos?: TablePhotoOption[];
  /**
   * The row whose photo picker is open, if it is in this table. Held by the
   * editor, not by the page: a page is one component per page number, and a
   * row that moves to another page (its Photo cell wraps, or a row above it is
   * removed) would take the picker away mid-use, or leave two open.
   */
  pickerRowId?: string | null;
  onPickerRowChange?: (rowId: string | null) => void;
}

/**
 * One page of a table appendix: the title (first page only) and a slice of
 * the rows under the repeated column header.
 *
 * Rendered THREE ways from the same markup: offscreen and whole in the
 * editor's `.measurement-appendix` box, where DocumentEditor reads the title,
 * head and each row's height; on the visible appendix pages, one row range
 * each; and mirrored by DocumentPDF's renderTableAppendixPage, which draws
 * the same tokens in points. The row heights measured here are what decide
 * the page breaks, so nothing that only appears while editing may take
 * layout space -- the row actions sit in the right margin, absolutely
 * positioned, for exactly that reason.
 *
 * Cells are LiveText plain text: one element whether editing or not (the
 * measurement copy renders with isEditing=false and must measure the element
 * the page draws), committed as typed -- never only on blur, see LiveText for
 * the loss that rule prevents -- with Shift+Enter moving to the next row.
 * Every text cell carries a layout key so the export can replay its browser
 * line breaks into the PDF -- a narrow column is exactly where react-pdf's
 * breaker and Chrome's disagree -- and the same key is the cell's caret
 * restore key, so a row that moves to the next page mid-edit keeps the caret.
 */
const TableAppendixPage: React.FC<TableAppendixPageProps> = ({
  block,
  blocks,
  numbers: numbersProp,
  fragment,
  isEditing,
  onChange,
  photos = [],
  pickerRowId = null,
  onPickerRowChange,
}) => {
  const data = block.data as TableAppendixBlockData;
  const allRows = useMemo(() => data.rows || [], [data.rows]);
  // Every column, hidden ones included, for writes; the visible ones for
  // drawing -- a hidden Item or Photo column is off the page and the PDF.
  const allColumns = useMemo(() => data.columns || [], [data.columns]);
  const columns = visibleTableColumns(allColumns);
  const isFirst = !fragment || fragment.part === 0;
  const firstIndex = fragment ? fragment.rowStart : 0;
  const rows = fragment ? allRows.slice(fragment.rowStart, fragment.rowEnd) : allRows;
  const numbers = useMemo(() => numbersProp ?? photoNumbers(blocks), [numbersProp, blocks]);
  const items = itemNumbers(allRows);

  const update = useCallback((patch: Partial<TableAppendixBlockData>) => {
    onChange?.({ ...data, ...patch });
  }, [data, onChange]);

  // The rows as last EMITTED, not as last rendered. A blur commit and the
  // row navigation it triggers happen in the same tick, before React has
  // re-rendered with the commit; a second write built from the rendered rows
  // would carry the old cell text and overwrite the commit. Every row write
  // reads from and updates this ref, so the second sees the first.
  const rowsRef = useRef(allRows);
  rowsRef.current = allRows;
  const emitRows = useCallback((rows: TableAppendixRow[]) => {
    rowsRef.current = rows;
    update({ rows });
  }, [update]);

  const updateRow = useCallback((rowId: string, patch: Partial<TableAppendixRow>) => {
    emitRows(rowsRef.current.map(r => (r.id === rowId ? { ...r, ...patch } : r)));
  }, [emitRows]);

  const insertBeside = useCallback((rowId: string, row: TableAppendixRow, where: 'before' | 'after') => {
    const current = rowsRef.current;
    const index = current.findIndex(r => r.id === rowId);
    const next = [...current];
    next.splice(where === 'before' ? index : index + 1, 0, row);
    emitRows(next);
  }, [emitRows]);

  const removeRow = useCallback((rowId: string) => {
    emitRows(rowsRef.current.filter(r => r.id !== rowId));
  }, [emitRows]);

  // One photo, one item: ticking a photo another row has moves it here, and
  // that row goes if it is left empty (setRowPhoto).
  const togglePicker = useCallback((rowId: string) => {
    onPickerRowChange?.(pickerRowId === rowId ? null : rowId);
  }, [onPickerRowChange, pickerRowId]);
  const closePicker = useCallback(() => onPickerRowChange?.(null), [onPickerRowChange]);
  const togglePhoto = useCallback((rowId: string, imageId: string, linked: boolean) => {
    emitRows(setRowPhoto(
      rowsRef.current, rowId, imageId, linked, allColumns,
      id => isTableRowOnAPlan(blocks, block.id, id)
    ));
  }, [emitRows, allColumns, blocks, block.id]);
  const photoColumn = columns.find(c => c.kind === 'image');
  const itemShown = columns.some(c => c.kind === 'item');

  const commitCell = useCallback((row: TableAppendixRow, column: TableAppendixColumn, text: string) => {
    if ((row.cells[column.id] || '') === text) return;
    updateRow(row.id, { cells: { ...row.cells, [column.id]: text } });
  }, [updateRow]);

  /**
   * Put the caret in a row's cell -- the same column when the row has it and
   * it is editable (a linked Image cell is not), otherwise the row's first
   * editable cell (a section heading has only the one). Searched across the
   * whole editor rather than this page, because the next row may be the
   * first row of the next page. Returns false when the row is not in the
   * DOM yet.
   */
  const focusCell = useCallback((rowId: string, columnId?: string): boolean => {
    const rowEl = window.document.querySelector<HTMLElement>(`.editor-main [data-table-row="${rowId}"]`);
    if (!rowEl) return false;
    const target =
      (columnId ? rowEl.querySelector<HTMLElement>(`[data-column-id="${columnId}"][contenteditable="true"]`) : null)
      ?? rowEl.querySelector<HTMLElement>('.table-appendix__text[contenteditable="true"]');
    if (!target) return false;
    target.focus();
    const sel = window.getSelection();
    if (sel) {
      const r = window.document.createRange();
      r.selectNodeContents(target);
      r.collapse(false);
      sel.removeAllRanges();
      sel.addRange(r);
    }
    return true;
  }, []);

  // A row added by Shift+Enter or the row actions is focused once it has
  // rendered. ONE attempt, in the render that carries the new rows: the
  // paginator places a row the moment it exists (heights are looked up by row
  // id, an unmeasured row borrowing a measured height), so the row is in the
  // DOM by now if it is going to be. A pending focus left waiting would fire
  // on some later, unrelated edit and pull the caret away from the author.
  const pendingFocusRef = useRef<{ rowId: string; columnId?: string } | null>(null);
  useEffect(() => {
    const pending = pendingFocusRef.current;
    if (!pending) return;
    pendingFocusRef.current = null;
    focusCell(pending.rowId, pending.columnId);
  }, [allRows, focusCell]);

  /**
   * Shift+Enter: on to the next row, same column -- and past the last row a
   * new one, so a schedule can be typed straight down without the mouse.
   * `fromRowId` null means the header row, whose next row is the first.
   */
  const moveToNextRow = useCallback((fromRowId: string | null, columnId?: string) => {
    const current = rowsRef.current;
    const index = fromRowId ? current.findIndex(r => r.id === fromRowId) : -1;
    const next = current[index + 1];
    if (next) {
      focusCell(next.id, columnId);
      return;
    }
    const added = blankTableRow();
    pendingFocusRef.current = { rowId: added.id, columnId };
    if (fromRowId) insertBeside(fromRowId, added, 'after');
    else emitRows([...current, added]);
  }, [focusCell, insertBeside, emitRows]);

  /**
   * Insert plain text at the caret of a cell, by editing the cell's text
   * directly rather than through execCommand. Chrome's own newline insertion
   * in a pre-wrap contentEditable is not deterministic -- at the end of the
   * text it doubles the newline so the caret has a line to sit on, and the
   * spare one survives into the content -- so a cell ends up with two blank
   * lines for two presses of Enter. Here a newline is one character, and a
   * ZERO-WIDTH SPACE after a trailing newline is what gives the caret its
   * line; the blur settle strips it. (React's re-render does NOT: when
   * the committed text equals what was stored, nothing changes and React
   * leaves the DOM as it is.)
   */
  const insertAtCaret = (el: HTMLElement, insert: string) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const offsetOf = (container: Node, offset: number) => {
      const r = window.document.createRange();
      r.selectNodeContents(el);
      r.setEnd(container, offset);
      return r.toString().length;
    };
    if (el.querySelector('sub')) {
      // Rewriting textContent below would flatten the subscript into the
      // text around it, so a cell that has one is edited in place.
      const tail = window.document.createRange();
      tail.selectNodeContents(el);
      tail.setStart(range.endContainer, range.endOffset);
      const atEnd = tail.toString().replace(/\u200B/g, '').length === 0;
      range.deleteContents();
      const node = window.document.createTextNode(insert + (/\n$/.test(insert) && atEnd ? '\u200B' : ''));
      range.insertNode(node);
      const r = window.document.createRange();
      r.setStart(node, insert.length);
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
      notifyLiveTextInput(el);
      return;
    }
    const text = el.textContent || '';
    const start = offsetOf(range.startContainer, range.startOffset);
    const end = offsetOf(range.endContainer, range.endOffset);
    const after = text.slice(end).replace(/\u200B/g, '');
    const before = text.slice(0, start).replace(/\u200B/g, '');
    const holder = /\n$/.test(insert) && after.length === 0 ? '\u200B' : '';
    el.textContent = before + insert + holder + after;
    const caret = before.length + insert.length;
    const node = el.firstChild;
    if (node) {
      const r = window.document.createRange();
      r.setStart(node, Math.min(caret, (node.textContent || '').length));
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
    }
    // A script edit fires no input event; the cell commits on this instead.
    notifyLiveTextInput(el);
  };

  // A cell holds several lines: Enter inserts a newline CHARACTER (the cell
  // is white-space: pre-wrap, so it renders as a break, the line measurement
  // reads it as one, and the PDF replays it). Not a <br> or a <div>, which
  // would put markup into what both surfaces treat as plain text. Tab moves
  // on, natively.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (e.shiftKey) {
      const el = e.currentTarget;
      const rowId = el.closest<HTMLElement>('[data-table-row]')?.dataset.tableRow ?? null;
      // Blur first so the cell commits before the move re-renders anything.
      el.blur();
      moveToNextRow(rowId, el.dataset.columnId);
      return;
    }
    insertAtCaret(e.currentTarget, '\n');
  };

  // Headers and section headings are one line: Enter finishes the edit;
  // Shift+Enter moves down into the rows.
  const handleSingleLineKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const el = e.currentTarget;
    el.blur();
    if (e.shiftKey) {
      const rowId = el.closest<HTMLElement>('[data-table-row]')?.dataset.tableRow ?? null;
      moveToNextRow(rowId, el.dataset.columnId);
    }
  };

  // Plain text only: a cell holds text nodes and nothing else, which is what
  // the line measurement and the PDF both expect of it. Line breaks are kept.
  const handlePaste = (e: React.ClipboardEvent<HTMLElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain').replace(/\r\n?/g, '\n');
    insertAtCaret(e.currentTarget, text);
  };

  /**
   * What a contentEditable holds, as the text it renders: innerText turns a
   * <br> the browser may have slipped in into a newline and keeps the ones
   * typed. The caret holder and a trailing newline are not content.
   */
  const readText = (el: HTMLElement): string =>
    // A cell holding a subscript is read as markup (subscript.ts); its
    // newlines are characters (see insertAtCaret), so its text nodes hold them.
    (el.querySelector('sub') ? readSubscripts(el) : el.innerText)
      .replace(/\u200B/g, '').replace(/\r/g, '').replace(/\n+$/, '');

  // Every editable text here is a LiveText with readText as its `read` and
  // settleOnBlur: Enter at the end of a cell leaves a newline and a caret
  // holder in the DOM that readText drops, and since the committed text then
  // equals the stored text nothing would re-render the cell. Settling puts
  // the DOM back to exactly the committed text on blur, so the visible row
  // never keeps a line the data does not have -- taller than the measured
  // copy that paginated it, and replayed into the PDF as an extra line.

  const cellStyle = (column: TableAppendixColumn): React.CSSProperties => ({
    flex: `0 0 ${column.widthPct}%`,
    maxWidth: `${column.widthPct}%`,
  });

  const renderCell = (row: TableAppendixRow, column: TableAppendixColumn) => {
    if (column.kind === 'item') {
      const index = allRows.indexOf(row);
      return (
        <div key={column.id} className="table-appendix__cell table-appendix__cell--item" style={cellStyle(column)}>
          <div className="table-appendix__text">{items[index] ?? ''}</div>
        </div>
      );
    }

    const linked = column.kind === 'image' && row.imageIds.length > 0;
    const text = column.kind === 'image'
      ? tableImageCell(row, column, numbers)
      : (row.cells[column.id] || '');
    const editable = isEditing && !linked;
    const layoutKey = tableCellLayoutKey(block.id, row.id, column.id);

    const openPicker = linked && isEditing;
    return (
      <div
        key={column.id}
        className={`table-appendix__cell${linked ? ' table-appendix__cell--linked' : ''}`}
        style={cellStyle(column)}
        data-photo-cell={column.kind === 'image' ? '' : undefined}
        data-photo-picker-trigger={openPicker ? '' : undefined}
        role={openPicker ? 'button' : undefined}
        tabIndex={openPicker ? 0 : undefined}
        aria-haspopup={openPicker ? 'dialog' : undefined}
        onClick={openPicker ? () => togglePicker(row.id) : undefined}
        onKeyDown={openPicker ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePicker(row.id); }
        } : undefined}
        title={linked
          ? `Filled from the photo appendix: ${PHOTO_LABEL} ${formatImageRefs(row.imageIds.map(id => numbers.get(id)).filter((n): n is number => n != null))}. It follows the photos if they are reordered.${isEditing ? ' Click to change which photos.' : ''}`
          : undefined}
      >
        <LiveText
          as="div"
          className="table-appendix__text"
          data-layout-key={layoutKey}
          data-column-id={column.id}
          value={text}
          editable={editable}
          read={readText}
          settleOnBlur
          restoreKey={layoutKey}
          onCommit={(committed) => commitCell(row, column, committed)}
          onKeyDown={editable ? handleKeyDown : undefined}
          onPaste={editable ? handlePaste : undefined}
        />
      </div>
    );
  };

  /** A blank row above or below this one, with the caret put in it. */
  const addRow = (rowId: string, where: 'before' | 'after') => {
    const added = blankTableRow();
    pendingFocusRef.current = { rowId: added.id };
    insertBeside(rowId, added, where);
  };

  /**
   * Why this row cannot be turned into a section heading, or null when it
   * can. Only an empty row converts: a heading shows no cells, so a row's
   * content would be hidden, and a row placed on a location plan would leave
   * an empty pin there. A heading can always become a row.
   */
  const headingRefusal = (row: TableAppendixRow): string | null => {
    if (row.kind === 'section') return null;
    if (tableRowHasContent(row, allColumns)) return 'Only an empty row can become a section heading';
    if (isTableRowOnAPlan(blocks, block.id, row.id)) {
      return 'This row is on a location plan -- take it off the plan to make it a heading';
    }
    return null;
  };

  /**
   * Empty row <-> section heading, in place. A cell of this row being typed
   * in is blurred first, which commits it into rowsRef synchronously: the
   * check then sees what is on screen, so a row whose words were not yet
   * committed is refused like any other with content, and a heading's
   * uncommitted text goes into the row rather than being lost as the heading
   * cell unmounts.
   */
  const toggleKind = (rowId: string) => {
    const active = window.document.activeElement;
    if (active instanceof HTMLElement && active.closest(`[data-table-row="${rowId}"]`)) active.blur();
    const row = rowsRef.current.find(r => r.id === rowId);
    if (!row || headingRefusal(row)) return;
    // Back into a row: the caret goes to the column the heading's words went
    // to, not the row's first cell (often an empty Image cell).
    pendingFocusRef.current = {
      rowId,
      columnId: row.kind === 'section' ? headingColumn(columns)?.id : undefined,
    };
    emitRows(rowsRef.current.map(r => (r.id === rowId ? toggleTableRowKind(r, columns) : r)));
  };

  const renderActions = (row: TableAppendixRow) => {
    if (!isEditing) return null;
    const refusal = headingRefusal(row);
    const toggleLabel = refusal ?? (row.kind === 'section' ? 'Turn into a row' : 'Turn into a section heading');
    // The two add-row icons are a row (the bar) with a plus on the side the
    // new one goes, so above and below read apart at a glance.
    return (
      <div className="table-appendix__row-actions" contentEditable={false}>
        <button
          type="button"
          title="Add a row above"
          aria-label="Add a row above"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => addRow(row.id, 'before')}
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 1.5v6M5 4.5h6" />
            <rect x="2" y="10" width="12" height="4.5" rx="1" />
          </svg>
        </button>
        <button
          type="button"
          title="Add a row below"
          aria-label="Add a row below"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => addRow(row.id, 'after')}
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="1.5" width="12" height="4.5" rx="1" />
            <path d="M8 8.5v6M5 11.5h6" />
          </svg>
        </button>
        <button
          type="button"
          title={toggleLabel}
          aria-label={toggleLabel}
          disabled={refusal !== null}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => toggleKind(row.id)}
        >
          {row.kind === 'section' ? (
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4.5" width="12" height="7" rx="1" />
              <path d="M6 4.5v7M10 4.5v7" />
            </svg>
          ) : (
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M4 3v10M12 3v10M4 8h8" />
            </svg>
          )}
        </button>
        {row.kind === 'row' && photoColumn && (
          <button
            type="button"
            title="Choose this row's photos"
            aria-label="Choose this row's photos"
            data-photo-picker-trigger=""
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => togglePicker(row.id)}
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1.5" y="3" width="13" height="10" rx="1.5" />
              <circle cx="5.5" cy="6.5" r="1.25" />
              <path d="M14.5 11l-3.5-3.5L4 13" />
            </svg>
          </button>
        )}
        <button
          type="button"
          title="Remove this row"
          aria-label="Remove this row"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => removeRow(row.id)}
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </div>
    );
  };

  return (
    <div className="apx-content">
      {isFirst && (
        <LiveText
          as="h2"
          className="apx-title"
          value={data.title || ''}
          editable={isEditing}
          read={readText}
          settleOnBlur
          restoreKey={`${block.id}:title`}
          data-placeholder="Appendix Title..."
          onCommit={(title) => update({ title })}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
        />
      )}

      <div className={`table-appendix${isEditing ? ' table-appendix--editing' : ''}`} data-table-appendix={block.id}>
        <div className="table-appendix__head">
          {columns.map(column => (
            <div
              key={column.id}
              className="table-appendix__cell table-appendix__cell--head"
              style={cellStyle(column)}
            >
              {/* The header is edited in place -- click it and type. One
                  element whether editing or not, like the cells. */}
              <LiveText
                as="div"
                className="table-appendix__text"
                data-column-id={column.id}
                value={column.label}
                editable={isEditing}
                read={readText}
                settleOnBlur
                restoreKey={`${block.id}:head:${column.id}`}
                data-placeholder="Column"
                onCommit={(label) => update({ columns: allColumns.map(c => (c.id === column.id ? { ...c, label } : c)) })}
                onKeyDown={isEditing ? handleSingleLineKeyDown : undefined}
                onPaste={isEditing ? handlePaste : undefined}
              />
            </div>
          ))}
        </div>

        <div className="table-appendix__rows">
          {rows.map((row, offset) => {
            const index = firstIndex + offset;
            const item = items[index];
            // Zebra by item number rather than by position on the page, so a
            // row keeps its stripe when the break above it moves.
            const zebra = item != null && item % 2 === 0;

            if (row.kind === 'section') {
              return (
                <div
                  key={row.id}
                  className="table-appendix__row table-appendix__row--section"
                  data-table-row={row.id}
                >
                  <div className="table-appendix__cell table-appendix__cell--section">
                    <LiveText
                      as="div"
                      className="table-appendix__text"
                      value={row.heading || ''}
                      editable={isEditing}
                      read={readText}
                      settleOnBlur
                      restoreKey={`${block.id}:${row.id}:heading`}
                      data-placeholder="Section heading..."
                      onCommit={(heading) => updateRow(row.id, { heading })}
                      onKeyDown={isEditing ? handleSingleLineKeyDown : undefined}
                      onPaste={isEditing ? handlePaste : undefined}
                    />
                  </div>
                  {renderActions(row)}
                </div>
              );
            }

            return (
              <div
                key={row.id}
                className={`table-appendix__row${zebra ? ' table-appendix__row--zebra' : ''}`}
                data-table-row={row.id}
              >
                {columns.map(column => renderCell(row, column))}
                {renderActions(row)}
              </div>
            );
          })}
        </div>
      </div>

      {isEditing && pickerRowId && photoColumn && rows.some(r => r.id === pickerRowId) && (() => {
        const row = allRows.find(r => r.id === pickerRowId)!;
        const index = allRows.indexOf(row);
        // Counted like the items (section headings take no number), so "row 4"
        // is the fourth row the author sees whether or not Item is shown.
        const name = (i: number) => `${itemShown ? 'item' : 'row'} ${items[i]}`;
        const elsewhere = new Map<string, string>();
        allRows.forEach((r, i) => {
          if (r.id !== row.id) r.imageIds.forEach(id => elsewhere.set(id, name(i)));
        });
        return (
          <RowPhotoPicker
            anchorSelector={`.document-preview [data-table-row="${row.id}"] [data-photo-cell]`}
            title={`Photos for ${itemShown ? 'item' : 'row'} ${items[index]}`}
            photos={photos}
            selected={row.imageIds}
            elsewhere={elsewhere}
            onToggle={(imageId, linked) => togglePhoto(row.id, imageId, linked)}
            onClear={() => updateRow(row.id, { imageIds: [] })}
            onClose={closePicker}
          />
        );
      })()}

      {isEditing && allRows.length === 0 && (
        <button
          type="button"
          className="table-appendix__add-row"
          onClick={() => update({ rows: [blankTableRow()] })}
        >
          + Add a row
        </button>
      )}
    </div>
  );
};

export default TableAppendixPage;
